/**
 * Generates a list of available years for a given metering point based on contract start dates.
 * The dropdown includes up to 3 years, depending on the contract history.
 *
 * @remark
 * The logic for "Up to 3 years" will be updated when the ext API response is updated
 * There will be a value for minimum year for the dropdown
 *
 * @param {Array<{ contractStart: string, meteringpointIdentifier: string }>} contracts - List of contracts.
 * @param {string} selectedMeteringPoint - The metering point identifier for filtering relevant contracts.
 * @returns {number[]} An array of available years for selection.
 */
export const getAvailableYears = (
  contracts: { contractStart: string; meteringpointIdentifier: string }[],
  selectedMeteringPoint: string
): number[] => {
  if (!contracts || contracts.length === 0) return []

  // Filter contracts based on the selected metering point identifier
  const relevantContracts = contracts.filter(
    contract => contract.meteringpointIdentifier === selectedMeteringPoint
  )

  if (relevantContracts.length === 0) return []

  // Get the current year
  const currentYear = new Date().getFullYear()

  // Determine the earliest contract start year for the selected metering point
  const earliestYear = relevantContracts
    .map(contract => new Date(contract.contractStart).getFullYear())
    .reduce((min, year) => Math.min(min, year), currentYear)

  // Ensure the start year is at least 2020
  const startYear = Math.max(earliestYear, 2020)

  // Generate the range of years from startYear to currentYear
  return Array.from({ length: currentYear - startYear + 1 }, (_, i) => startYear + i)
}
